import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {inject, observer} from 'mobx-react';
import HelpFloatingButtonUI from '../view/HelpFloatingButtonUI';
import {
  HELP_URL,
  HelpItem,
  HelpItemAction,
  helpItems,
} from '../config/helpItems';
import {resetAllTourSections} from '../../Tour/utils/tourStorage';
import NavigationStore from '../../../navigation/store/NavigationStore';
import AiAssistantChatbotStore from '../../AiAssistantChatbot/store/AiAssistantChatbotStore';
import {ORGANIZATION_USER_STAFF} from '../../../data/models/Oganization/Organization';
import {APP_PERMISSIONS} from '../../../data/permissions';
import {isLearner} from '../../../utils/roleHelper';

const actionHandlers: Record<HelpItemAction, () => void> = {
  reactivateTour: () => {
    resetAllTourSections();
    window.location.reload();
  },
  openHelp: () => {
    window.open(HELP_URL, '_blank', 'noopener,noreferrer');
  },
};

interface Props {
  navigationStore?: NavigationStore;
  aiAssistantChatbotStore?: AiAssistantChatbotStore;
}

const HelpFloatingButtonContainer: React.FC<Props> = ({
  navigationStore,
  aiAssistantChatbotStore,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pathname, setPathname] = useState(window.location.pathname);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const update = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', update);
    const originalPush = window.history.pushState;
    const originalReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      originalPush.apply(this, args);
      update();
    };
    window.history.replaceState = function (...args) {
      originalReplace.apply(this, args);
      update();
    };
    return () => {
      window.removeEventListener('popstate', update);
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
    };
  }, []);

  const hideForLearners = isLearner(
    navigationStore?.userOrganization?.userType,
  );

  const hideForRoute =
    pathname.startsWith('/editor') ||
    pathname.startsWith('/preview') ||
    pathname.startsWith('/subscription_tier') ||
    pathname.startsWith('/scorm') ||
    pathname.startsWith('/player') ||
    pathname.startsWith('/auth') ||
    /^\/course\/[^/]+\/lesson\/[^/]+/.test(pathname) ||
    /^\/[^/]+\/ai_assistant\/[^/]+/.test(pathname);

  const canReactivateTour =
    navigationStore?.userOrganization?.userType === ORGANIZATION_USER_STAFF &&
    navigationStore?.userPermissions.includes(
      APP_PERMISSIONS.ORG_MANAGE_SETTINGS,
    );

  const visibleItems = useMemo(
    () =>
      helpItems.filter(
        item => item.action !== 'reactivateTour' || canReactivateTour,
      ),
    [canReactivateTour],
  );

  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        close();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, close]);

  const handleItemClick = useCallback((item: HelpItem) => {
    actionHandlers[item.action]();
    setIsOpen(false);
  }, []);

  if (
    hideForLearners ||
    hideForRoute ||
    aiAssistantChatbotStore?.shouldShowChatbot
  )
    return null;

  return (
    <HelpFloatingButtonUI
      isOpen={isOpen}
      items={visibleItems}
      containerRef={containerRef}
      onToggle={toggle}
      onItemClick={handleItemClick}
    />
  );
};

export default inject(
  'navigationStore',
  'aiAssistantChatbotStore',
)(observer(HelpFloatingButtonContainer));
